/**
 * Impact Dog Crates - Crate Quiz
 * 
 * Complete quiz logic including:
 * - Step navigation with conditional logic
 * - Breed autocomplete with size mapping (loads from dog-breed-sizes.json)
 * - Product recommendation engine
 * - Klaviyo/GA4 integration
 */

(function() {
  'use strict';

  // ============================================
  // BREED DATABASE - LOADED FROM JSON
  // ============================================
  
  // Will be populated from dog-breed-sizes.json
  let breedDatabase = [];
  let breedDataLoaded = false;

  // URL for breed data - set this in Liquid or use asset_url
  const BREED_DATA_URL = window.breedSizeDataUrl || '/cdn/shop/files/dog-breed-sizes.json';

  /**
   * Load breed data from JSON file
   */
  async function loadBreedData() {
    try {
      const response = await fetch(BREED_DATA_URL);
      if (!response.ok) throw new Error('Failed to load breed data');
      breedDatabase = await response.json();
      breedDataLoaded = true;
      console.log(`Loaded ${breedDatabase.length} breeds from database`);
      return true;
    } catch (error) {
      console.error('Error loading dog breed sizes:', error);
      return false;
    }
  }

  /**
   * Extract clean size from raw size string
   * e.g., "34\" (Intermediate)" → "34"
   * e.g., "40\"+ (Extra Large)" → "40+"
   * e.g., "40\"+ (Extra Large) or 48\" (Double Extra Large)" → "40+" (first/smaller size)
   */
  function extractSizeOnly(rawSize) {
    if (!rawSize) return null;
    const match = rawSize.match(/^([0-9]+("|″)?\+?)/);
    return match ? match[1].replace(/"|″/g, '') : null;
  }

  /**
   * Search for a breed using the same matching logic as dog-sizes.js
   * Priority: exact → alias → partial → partialAlias
   * Returns: { breed: object, matchType: string, size: string } or null
   */
  function findBreed(query) {
    if (!breedDataLoaded || !query) return null;
    
    const searchQuery = query.toLowerCase().trim();
    
    // 1. Exact match on breed name
    const exact = breedDatabase.find(b => 
      b['Dog breed'].toLowerCase() === searchQuery
    );
    if (exact) {
      return {
        breed: exact,
        matchType: 'exact',
        size: extractSizeOnly(exact['Impact Dog Crate Size'])
      };
    }

    // 2. Exact match on alias
    const aliasMatch = breedDatabase.find(b => {
      if (!b.Aliases || !Array.isArray(b.Aliases)) return false;
      return b.Aliases.some(alias => alias.toLowerCase() === searchQuery);
    });
    if (aliasMatch) {
      return {
        breed: aliasMatch,
        matchType: 'alias',
        size: extractSizeOnly(aliasMatch['Impact Dog Crate Size'])
      };
    }

    // 3. Partial match on breed name
    const partial = breedDatabase.find(b => 
      b['Dog breed'].toLowerCase().includes(searchQuery)
    );
    if (partial) {
      return {
        breed: partial,
        matchType: 'partial',
        size: extractSizeOnly(partial['Impact Dog Crate Size'])
      };
    }

    // 4. Partial match on alias
    const partialAlias = breedDatabase.find(b => {
      if (!b.Aliases || !Array.isArray(b.Aliases)) return false;
      return b.Aliases.some(alias => alias.toLowerCase().includes(searchQuery));
    });
    if (partialAlias) {
      return {
        breed: partialAlias,
        matchType: 'partialAlias',
        size: extractSizeOnly(partialAlias['Impact Dog Crate Size'])
      };
    }

    return null;
  }

  /**
   * Search breeds for autocomplete dropdown
   * Returns array of matching breeds with their sizes
   */
  function searchBreeds(query, limit = 10) {
    if (!breedDataLoaded || !query || query.length < 2) return [];
    
    const searchQuery = query.toLowerCase().trim();
    const results = [];
    const seen = new Set();

    // Helper to add a breed if not already seen
    const addBreed = (breed, matchType) => {
      const name = breed['Dog breed'];
      if (!seen.has(name)) {
        seen.add(name);
        results.push({
          name: name,
          size: breed['Impact Dog Crate Size'],
          cleanSize: extractSizeOnly(breed['Impact Dog Crate Size']),
          matchType: matchType
        });
      }
    };

    // Search in order of priority
    // 1. Breeds starting with query (best matches first)
    breedDatabase.forEach(breed => {
      if (breed['Dog breed'].toLowerCase().startsWith(searchQuery)) {
        addBreed(breed, 'startsWith');
      }
    });

    // 2. Breeds containing query
    breedDatabase.forEach(breed => {
      if (breed['Dog breed'].toLowerCase().includes(searchQuery)) {
        addBreed(breed, 'contains');
      }
    });

    // 3. Alias matches
    breedDatabase.forEach(breed => {
      if (breed.Aliases && Array.isArray(breed.Aliases)) {
        const aliasMatch = breed.Aliases.some(alias => 
          alias.toLowerCase().includes(searchQuery)
        );
        if (aliasMatch) {
          addBreed(breed, 'alias');
        }
      }
    });

    return results.slice(0, limit);
  }

  /**
   * Get size for a breed by name
   */
  function getBreedSize(breedName) {
    const result = findBreed(breedName);
    return result ? result.size : null;
  }

  // Weight to size mapping (fallback for mixed/unknown breeds)
  const WEIGHT_TO_SIZE = {
    'under-25': '24',
    '25-50': '30',
    '50-85': '34',
    '85-150': '40+',
    'over-150': '54'
  };

  // ============================================
  // PRODUCT DATA
  // ============================================

  const PRODUCTS = {
    'high-anxiety': {
      title: 'High Anxiety Crate',
      handle: 'high-anxiety-crate-xl',
      description: 'No dog breaks out. Period. The strongest, most secure crate ever built for dogs with a history of escaping.',
      features: ['100% Escape Proof', 'Lifetime Dog Damage Guarantee', '62% Thicker Aluminum', '4 Butterfly Latches'],
      sizes: ['30', '34', '40', '40+', '48'],
      image: 'high-anxiety-crate.jpg'
    },
    'collapsible-v2': {
      title: 'Collapsible Dog Crate',
      handle: 'collapsible-dog-crate',
      description: 'Built to perform, ready in under 60 seconds. Folds flat for travel and storage.',
      features: ['Sets Up in 60 Seconds', 'Folds Flat', 'Airline Hardware Included', 'Escape Resistant'],
      sizes: ['30', '34', '40', '40+', '48', '54'],
      image: 'collapsible-dog-crate.jpg'
    },
    'airline': {
      title: 'Airline Dog Crate',
      handle: 'airline-dog-crate',
      description: 'Built to fly. Designed to meet IATA CR82 requirements with built-in airline brackets.',
      features: ['IATA CR82 Compliant', 'Built-in Airline Brackets', 'Padlock Ready', '2 Butterfly Latches'],
      sizes: ['34', '40', '40+'],
      image: 'airline-dog-crate.jpg'
    },
    'dog-den': {
      title: 'Impact Dog Den',
      handle: 'the-impact-den',
      description: 'A stylish, modern dog den for calm dogs. Removable door, vaulted ceiling, and easy cleaning.',
      features: ['Removable Door', 'Vaulted Ceiling', 'Modern Design', 'Removable Tray'],
      sizes: ['24', '34', '48'],
      image: 'impact-dog-den.jpg'
    },
    'mini-stationary': {
      title: 'Mini Stationary Crate',
      handle: 'mini-stationary',
      description: 'Compact, heavy-duty aluminum crate built for smaller dogs.',
      features: ['Ships Assembled', 'Compact Design', 'Lifetime Warranty', 'Padlock Ready'],
      sizes: ['24'],
      image: 'mini-stationary.jpg'
    }
  };

  // Size label mapping
  const SIZE_LABELS = {
    '20': 'Extra Small (20")',
    '24': 'Small (24")',
    '30': 'Medium (30")',
    '34': 'Intermediate (34")',
    '40': 'Large (40")',
    '40+': 'Extra Large (40+")',
    '48': '2XL (48")',
    '54': '3XL (54")'
  };

  // Caveat messages
  const CAVEATS = {
    // ============================================
    // GIANT DOG (54") CAVEATS
    // ============================================
    'giant_breed_escape_warning': 'The Collapsible is our only crate in 54". While it\'s escape-resistant for most dogs, it\'s not rated for severe escape artists like our High Anxiety crate. If your dog has a serious escape history, please chat with our team.',
    'giant_breed_flying_warning': 'The Collapsible is our only crate in 54" and it\'s not IATA-approved for airline cargo. You\'ll need to work directly with your airline on approved options for giant breeds.',
    'giant_escape_and_flying': 'For giant breeds with escape concerns who fly, we don\'t have a perfect solution. The Collapsible 54" is our only crate in this size—it\'s not escape-proof like our High Anxiety (which maxes out at 48") and it\'s not IATA-approved. We recommend chatting with our team to discuss your options.',
    
    // ============================================
    // SMALL DOG (24") CAVEATS
    // ============================================
    'small_dog_escape_concern': 'The Mini Stationary is the right size for your dog and great for car travel, but it\'s not rated for severe escape artists. Our escape-proof High Anxiety crate starts at 30" which may be too large—measure your dog carefully if considering that option.',
    'small_dog_airline_cabin': 'The Mini Stationary is perfect for home and car travel, but it\'s not IATA-approved. For air travel, small dogs typically fly in-cabin using airline-approved soft-sided carriers.',
    'small_dog_escape_and_flying': 'For small dogs with escape concerns who fly, we don\'t have a perfect solution. The Mini Stationary (24") is the right size but isn\'t escape-proof or IATA-approved. Our Airline Crate starts at 34" (too large) and small dogs typically fly in-cabin anyway. We recommend chatting with our team.',
    
    // ============================================
    // STANDARD SIZE CAVEATS
    // ============================================
    'escape_concern_flying_warning': 'The Airline Crate meets IATA requirements but isn\'t escape-proof. For severe escape artists, we recommend the High Anxiety for home and discussing cargo options with your airline.',
    'no_airline_size_available': 'Our Airline Crate doesn\'t come in your dog\'s size. You\'ll need to work directly with your airline on approved cargo options.',
    'collapsible_escape_warning': 'The Collapsible is escape-resistant for most dogs, but not rated for severe escape artists. If your dog has broken out of heavy-duty crates before, the High Anxiety is the safer choice for home.'
  };

  // ============================================
  // QUIZ STATE
  // ============================================

  let quizState = {
    currentStep: 1,
    totalSteps: 11,
    answers: {
      dogName: null,
      concerns: [],
      breed: null,
      breedSize: null, // Store the determined size from breed lookup
      weight: null,
      travel: null,
      travelType: null,
      priority: null,
      email: null
    },
    recommendation: null
  };

  // Track which steps are active (some are conditional)
  let activeSteps = [1, 2, 3, 4, 6, 7, 10, 11]; // Default: skip weight (5), travel type (8), priority (9)

  // ============================================
  // DOM ELEMENTS
  // ============================================

  let elements = {};
  const announcementFrames = new WeakMap();
  let breedActiveIndex = -1;
  let breedOptions = [];
  let breedSearchTimer = null;
  let emailValidationAttempted = false;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function announce(element, message) {
    if (!element) return;

    element.textContent = '';
    const previousFrame = announcementFrames.get(element);
    if (previousFrame) {
      cancelAnimationFrame(previousFrame);
    }
    const nextFrame = requestAnimationFrame(() => {
      element.textContent = message;
      announcementFrames.delete(element);
    });
    announcementFrames.set(element, nextFrame);
  }

  function cacheElements() {
    const section = document.querySelector('.crate-quiz');
    if (!section) return false;

    elements = {
      section,
      progressBar: section.querySelector('[data-progress-bar]'),
      progressFill: section.querySelector('[data-progress-fill]'),
      progressText: section.querySelector('[data-progress-text]'),
      quizStatus: section.querySelector('[data-quiz-status]'),
      steps: section.querySelectorAll('.quiz-step'),
      dogNameInput: section.querySelector('[data-dog-name-input]'),
      breedInput: section.querySelector('[data-breed-input]'),
      breedDropdown: section.querySelector('[data-breed-dropdown]'),
      breedStatus: section.querySelector('[data-breed-status]'),
      breedValue: section.querySelector('[data-breed-value]'),
      mixedBreedButton: section.querySelector('[data-select-mixed]'),
      emailInput: section.querySelector('[data-email-input]'),
      emailError: section.querySelector('[data-email-error]'),
      // Results elements
      resultsPrimary: section.querySelector('[data-results-primary]'),
      resultsSecondary: section.querySelector('[data-results-secondary]'),
      resultsCaveat: section.querySelector('[data-results-caveat]')
    };

    return true;
  }

  // ============================================
  // STEP NAVIGATION
  // ============================================

  function updateProgress() {
    const currentIndex = activeSteps.indexOf(quizState.currentStep);
    const totalActive = activeSteps.length;
    const percentage = ((currentIndex + 1) / totalActive) * 100;
    const progressMessage = `Step ${currentIndex + 1} of ${totalActive}`;
    
    if (elements.progressFill) {
      elements.progressFill.style.width = `${percentage}%`;
    }
    if (elements.progressText) {
      elements.progressText.textContent = progressMessage;
    }
    if (elements.progressBar) {
      elements.progressBar.setAttribute('aria-valuemax', String(totalActive));
      elements.progressBar.setAttribute('aria-valuenow', String(currentIndex + 1));
      elements.progressBar.setAttribute('aria-valuetext', progressMessage);
    }

    return progressMessage;
  }

  function showStep(stepNumber, options = {}) {
    const {
      shouldAnnounce = true,
      shouldFocus = true,
      shouldScroll = true,
      shouldTrack = true
    } = options;
    let activeStep = null;

    elements.steps.forEach(step => {
      const isActive = parseInt(step.dataset.step) === stepNumber;
      step.classList.toggle('quiz-step--active', isActive);
      if (isActive) {
        activeStep = step;
        step.hidden = false;
        step.removeAttribute('inert');
        step.setAttribute('data-step-active', '');
      } else {
        step.removeAttribute('data-step-active');
        step.setAttribute('inert', '');
        step.hidden = true;
      }
    });

    quizState.currentStep = stepNumber;
    const progressMessage = updateProgress();

    // Scroll to top of quiz
    if (shouldScroll) {
      elements.section.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start'
      });
    }

    if (shouldFocus && activeStep) {
      const heading = activeStep.querySelector('[data-step-heading]');
      requestAnimationFrame(() => heading?.focus({ preventScroll: true }));
    }

    if (shouldAnnounce) {
      announce(elements.quizStatus, progressMessage);
    }

    if (stepNumber !== 4) {
      hideBreedDropdown();
    }

    // Track step view
    if (shouldTrack) {
      trackEvent('quiz_step_view', { step: stepNumber });
    }
  }

  function nextStep() {
    const currentIndex = activeSteps.indexOf(quizState.currentStep);
    console.log('nextStep - current:', quizState.currentStep, 'index:', currentIndex, 'activeSteps:', activeSteps);
    if (currentIndex < activeSteps.length - 1) {
      const nextStepNum = activeSteps[currentIndex + 1];
      console.log('Going to step:', nextStepNum);
      showStep(nextStepNum);
    }
  }

  function prevStep() {
    const currentIndex = activeSteps.indexOf(quizState.currentStep);
    if (currentIndex > 0) {
      showStep(activeSteps[currentIndex - 1]);
    }
  }

  function updateActiveSteps() {
    // Start with base steps: dog name, concerns, did you know #1, breed
    activeSteps = [1, 2, 3, 4];

    // Add weight step (5) if breed is mixed/unknown
    if (quizState.answers.breed === 'mixed') {
      activeSteps.push(5);
      console.log('Mixed breed detected - adding weight step (5)');
    }

    // Always include Did You Know #2 interstitial (step 6)
    activeSteps.push(6);

    // Always include travel frequency (step 7)
    activeSteps.push(7);

    // Add travel type (8) if travels often
    const travelsOften = ['very-often', 'often'].includes(quizState.answers.travel);
    if (travelsOften) {
      activeSteps.push(8);
    }

    // Add priority question (9) if conflict detected
    if (detectConflict()) {
      activeSteps.push(9);
    }

    // Always end with email (10) and results (11)
    activeSteps.push(10, 11);

    if (elements.progressBar) {
      updateProgress();
    }
    
    console.log('Updated active steps:', activeSteps);
  }

  function detectConflict() {
    const hasEscapeConcern = quizState.answers.concerns.some(c => 
      ['escapes', 'anxiety', 'destroys'].includes(c)
    );
    const travelsOften = ['very-often', 'often'].includes(quizState.answers.travel);
    
    // Get the size to check for edge cases
    const { breed, breedSize, weight } = quizState.answers;
    let size = null;
    if (breed && breed !== 'mixed') {
      size = breedSize || getBreedSize(breed);
    } else if (weight) {
      size = WEIGHT_TO_SIZE[weight];
    }
    
    // Skip priority question for small dogs (24") and giant dogs (54")
    // because the recommendation doesn't change based on priority
    if (size === '24' || size === '20' || size === '54') {
      return false;
    }
    
    return hasEscapeConcern && travelsOften;
  }

  // ============================================
  // ANSWER HANDLING
  // ============================================

  function setOptionSelected(option, isSelected) {
    option.classList.toggle('quiz-option--selected', isSelected);
    option.setAttribute('aria-pressed', String(isSelected));
  }

  function handleOptionClick(e) {
    const option = e.target.closest('.quiz-option');
    if (!option) return;

    const container = option.closest('[data-question]');
    const question = container.dataset.question;
    const value = option.dataset.value;
    const isMulti = option.dataset.multi === 'true';

    if (isMulti) {
      // Toggle selection for multi-select
      setOptionSelected(option, !option.classList.contains('quiz-option--selected'));
      
      // Update answers array
      const currentAnswers = quizState.answers[question] || [];
      if (option.classList.contains('quiz-option--selected')) {
        quizState.answers[question] = [...currentAnswers, value];
      } else {
        quizState.answers[question] = currentAnswers.filter(v => v !== value);
      }

      // Handle "calm" being exclusive to other options
      if (value === 'calm' && option.classList.contains('quiz-option--selected')) {
        // Deselect other options
        container.querySelectorAll('.quiz-option').forEach(opt => {
          if (opt.dataset.value !== 'calm') {
            setOptionSelected(opt, false);
          }
        });
        quizState.answers[question] = ['calm'];
      } else if (value !== 'calm' && option.classList.contains('quiz-option--selected')) {
        // Deselect "calm" if another option is selected
        const calmOption = container.querySelector('[data-value="calm"]');
        if (calmOption) {
          setOptionSelected(calmOption, false);
          quizState.answers[question] = quizState.answers[question].filter(v => v !== 'calm');
        }
      }
    } else {
      // Single select
      container.querySelectorAll('.quiz-option').forEach(opt => {
        setOptionSelected(opt, false);
      });
      setOptionSelected(option, true);
      quizState.answers[question] = value;
    }

    // Update active steps after travel answer changes
    if (question === 'travel' || question === 'concerns') {
      updateActiveSteps();
    }

    // Enable/disable next button
    validateCurrentStep();

    // Track answer
    trackEvent('quiz_answer', { question, value: quizState.answers[question] });
  }

  function validateCurrentStep() {
    const currentStepEl = elements.section.querySelector(`[data-step="${quizState.currentStep}"]`);
    if (!currentStepEl) return;

    const nextBtn = currentStepEl.querySelector('[data-next]');
    if (!nextBtn) return;

    let isValid = false;
    const step = quizState.currentStep;

    switch(step) {
      case 1: // Dog's name
        isValid = quizState.answers.dogName !== null && quizState.answers.dogName.trim() !== '';
        break;
      case 2: // Behavioral concerns
        isValid = quizState.answers.concerns.length > 0;
        break;
      case 3: // Did You Know #1 - always valid
        isValid = true;
        break;
      case 4: // Breed
        isValid = quizState.answers.breed !== null;
        break;
      case 5: // Weight
        isValid = quizState.answers.weight !== null;
        break;
      case 6: // Did You Know #2 - always valid
        isValid = true;
        break;
      case 7: // Travel
        isValid = quizState.answers.travel !== null;
        break;
      case 8: // Travel type
        isValid = quizState.answers.travelType !== null;
        break;
      case 9: // Priority
        isValid = quizState.answers.priority !== null;
        break;
      case 10: // Email
        const email = elements.emailInput?.value || '';
        isValid = isValidEmail(email);
        break;
      default:
        isValid = true;
    }

    nextBtn.disabled = !isValid;
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function updateEmailValidation(showError) {
    if (!elements.emailInput) return false;
    const isValid = isValidEmail(elements.emailInput.value.trim());
    const shouldShowError = showError && !isValid;
    elements.emailInput.setAttribute('aria-invalid', shouldShowError ? 'true' : 'false');
    if (elements.emailError) {
      elements.emailError.hidden = !shouldShowError;
    }
    return isValid;
  }

  // ============================================
  // BREED AUTOCOMPLETE
  // ============================================

  function resetBreedActiveOption() {
    breedOptions.forEach(option => {
      option.classList.remove('quiz-breed-search__item--active');
      option.setAttribute('aria-selected', 'false');
    });
    breedActiveIndex = -1;
    elements.breedInput?.removeAttribute('aria-activedescendant');
  }

  function setBreedDropdownExpanded(isExpanded) {
    if (!elements.breedDropdown || !elements.breedInput) return;

    elements.breedDropdown.hidden = !isExpanded;
    elements.breedDropdown.classList.toggle('quiz-breed-search__dropdown--visible', isExpanded);
    elements.breedInput.setAttribute('aria-expanded', String(isExpanded));

    if (!isExpanded) {
      resetBreedActiveOption();
    }
  }

  function setActiveBreedOption(index) {
    if (breedOptions.length === 0) return;

    const normalizedIndex = (index + breedOptions.length) % breedOptions.length;
    breedOptions.forEach((option, optionIndex) => {
      const isActive = optionIndex === normalizedIndex;
      option.classList.toggle('quiz-breed-search__item--active', isActive);
      option.setAttribute('aria-selected', String(isActive));
    });

    breedActiveIndex = normalizedIndex;
    const activeOption = breedOptions[normalizedIndex];
    elements.breedInput.setAttribute('aria-activedescendant', activeOption.id);
    activeOption.scrollIntoView({ block: 'nearest' });
  }

  function renderBreedMessage(message) {
    const messageElement = document.createElement('div');
    messageElement.className = 'quiz-breed-search__no-results';
    messageElement.textContent = message;
    elements.breedDropdown.replaceChildren(messageElement);
    breedOptions = [];
    resetBreedActiveOption();
  }

  function handleBreedKeydown(event) {
    const query = elements.breedInput.value.trim();

    switch (event.key) {
      case 'ArrowDown':
        if (elements.breedDropdown.hidden) {
          showBreedResults(query);
        }
        if (!elements.breedDropdown.hidden && breedOptions.length > 0) {
          event.preventDefault();
          setActiveBreedOption(breedActiveIndex + 1);
        }
        break;
      case 'ArrowUp':
        if (elements.breedDropdown.hidden) {
          showBreedResults(query);
        }
        if (!elements.breedDropdown.hidden && breedOptions.length > 0) {
          event.preventDefault();
          setActiveBreedOption(breedActiveIndex < 0 ? breedOptions.length - 1 : breedActiveIndex - 1);
        }
        break;
      case 'Enter':
        if (!elements.breedDropdown.hidden && breedActiveIndex >= 0) {
          event.preventDefault();
          breedOptions[breedActiveIndex].click();
        }
        break;
      case 'Escape':
        if (!elements.breedDropdown.hidden) {
          event.preventDefault();
          hideBreedDropdown();
          announce(elements.breedStatus, 'Breed suggestions closed.');
        }
        break;
      case 'Tab':
        hideBreedDropdown();
        break;
    }
  }

  function initBreedSearch() {
    if (!elements.breedInput) return;

    elements.breedInput.addEventListener('input', (e) => {
      clearTimeout(breedSearchTimer);

      if (quizState.answers.breed !== null) {
        quizState.answers.breed = null;
        quizState.answers.breedSize = null;
        quizState.answers.weight = null;
        if (elements.breedValue) {
          elements.breedValue.value = '';
        }
        elements.mixedBreedButton?.setAttribute('aria-pressed', 'false');
        elements.breedInput.placeholder = 'Search for a breed...';
        updateActiveSteps();
      }

      validateCurrentStep();
      breedSearchTimer = setTimeout(() => {
        const query = e.target.value.trim();
        showBreedResults(query);
      }, 150);
    });

    elements.breedInput.addEventListener('keydown', handleBreedKeydown);

    elements.breedInput.addEventListener('focus', () => {
      if (elements.breedInput.value.length >= 2) {
        showBreedResults(elements.breedInput.value.trim());
      }
    });

    // Close dropdown on click outside
    document.addEventListener('click', (e) => {
      const breedSearch = elements.breedInput.closest('.quiz-breed-search');
      if (breedSearch && !breedSearch.contains(e.target)) {
        hideBreedDropdown();
      }
    });

    // Handle "mixed/unknown" button
    const mixedBtn = elements.mixedBreedButton;
    if (mixedBtn) {
      mixedBtn.addEventListener('click', () => {
        selectBreed('mixed', 'Mixed / Unknown', null);
      });
    }
  }

  function showBreedResults(query) {
    if (!breedDataLoaded) {
      renderBreedMessage('Loading breed data...');
      setBreedDropdownExpanded(true);
      announce(elements.breedStatus, 'Loading breed suggestions.');
      return;
    }

    if (query.length < 2) {
      hideBreedDropdown();
      return;
    }

    const matches = searchBreeds(query, 10);

    if (matches.length === 0) {
      renderBreedMessage(`No breeds found for "${query}". Try the full breed name or select "Mixed / Unknown" below.`);
      announce(elements.breedStatus, 'No breed suggestions found.');
    } else {
      const fragment = document.createDocumentFragment();
      matches.forEach((match, index) => {
        const option = document.createElement('div');
        const name = document.createElement('span');
        const size = document.createElement('span');

        option.id = `${elements.breedDropdown.id}-option-${index}`;
        option.className = 'quiz-breed-search__item';
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');
        option.dataset.breed = match.name;
        option.dataset.size = match.cleanSize || '';

        name.className = 'quiz-breed-search__name';
        name.textContent = match.name;
        size.className = 'quiz-breed-search__size';
        size.textContent = SIZE_LABELS[match.cleanSize] || match.size || '';

        option.append(name, size);
        option.addEventListener('click', () => {
          selectBreed(option.dataset.breed, option.dataset.breed, option.dataset.size);
        });
        option.addEventListener('mousemove', () => setActiveBreedOption(index));
        fragment.append(option);
      });

      elements.breedDropdown.replaceChildren(fragment);
      breedOptions = Array.from(elements.breedDropdown.querySelectorAll('[role="option"]'));
      resetBreedActiveOption();
      announce(
        elements.breedStatus,
        `${matches.length} breed ${matches.length === 1 ? 'suggestion' : 'suggestions'} available. Use the up and down arrow keys to review.`
      );
    }

    setBreedDropdownExpanded(true);
  }

  function hideBreedDropdown() {
    clearTimeout(breedSearchTimer);
    breedSearchTimer = null;
    setBreedDropdownExpanded(false);
  }

  function selectBreed(value, displayName, size) {
    quizState.answers.breed = value;
    quizState.answers.breedSize = size;
    
    if (value === 'mixed') {
      elements.breedInput.value = '';
      elements.breedInput.placeholder = 'Mixed / Unknown selected';
      // Clear any previous weight selection since they need to re-select
      quizState.answers.weight = null;
    } else {
      elements.breedInput.placeholder = 'Search for a breed...';
      elements.breedInput.value = displayName;
    }

    elements.mixedBreedButton?.setAttribute('aria-pressed', String(value === 'mixed'));
    
    if (elements.breedValue) {
      elements.breedValue.value = value;
    }
    
    hideBreedDropdown();
    announce(elements.breedStatus, `${displayName} selected.`);
    
    // IMPORTANT: Update active steps AFTER setting breed
    // This ensures step 4 (weight) is added for mixed breeds
    updateActiveSteps();
    validateCurrentStep();
    
    console.log('Breed selected:', value, 'Active steps:', activeSteps);

    // Track breed selection
    trackEvent('quiz_breed_selected', { 
      breed: value, 
      size: size,
      matchType: value === 'mixed' ? 'mixed' : 'selected'
    });
  }

  // ============================================
  // RECOMMENDATION ENGINE
  // ============================================

  function getRecommendation() {
    const { concerns, breed, breedSize, weight, travel, travelType, priority } = quizState.answers;

    // Determine size - use stored breedSize if available, otherwise look it up or use weight fallback
    let size;
    if (breed && breed !== 'mixed') {
      // Use the stored size from breed selection, or look it up
      size = breedSize || getBreedSize(breed) || '34';
    } else {
      // Mixed/unknown breed - use weight mapping
      size = WEIGHT_TO_SIZE[weight] || '34';
    }

    // Handle special "20" size (too small for our crates)
    if (size === '20') {
      size = '24'; // Bump up to smallest available
    }

    const hasEscapeConcern = concerns.some(c => 
      ['escapes', 'anxiety', 'destroys'].includes(c)
    );
    const isCalm = concerns.includes('calm') && !hasEscapeConcern;
    const travelsOften = ['very-often', 'often'].includes(travel);
    const fliesWithDog = travelType === 'flying' || travelType === 'both';
    const roadTrips = travelType === 'road' || travelType === 'both';

    let recommendation = {
      primary: null,
      secondary: null,
      caveat: null,
      showBothEqual: false
    };

    // =========================================================
    // GIANT BREED CONSTRAINT (54")
    // Only the Collapsible comes in 54" - it's the only option
    // =========================================================
    if (size === '54') {
      recommendation.primary = { product: 'collapsible-v2', size: '54' };
      
      // Add appropriate caveat based on their situation
      if (hasEscapeConcern && fliesWithDog) {
        recommendation.caveat = 'giant_escape_and_flying';
      } else if (hasEscapeConcern) {
        recommendation.caveat = 'giant_breed_escape_warning';
      } else if (fliesWithDog) {
        recommendation.caveat = 'giant_breed_flying_warning';
      }
      // No caveat needed for calm + road travel - Collapsible is perfect for that
      
      return recommendation;
    }

    // =========================================================
    // SMALL DOG PATHS (24" or smaller)
    // Mini Stationary is the right size for almost everything
    // Dog Den for calm dogs who don't travel
    // Just different caveats based on their needs
    // =========================================================
    if (size === '24' || size === '20') {
      
      // Calm small dog who doesn't travel → Dog Den is ideal
      if (!hasEscapeConcern && !travelsOften) {
        recommendation.primary = { product: 'dog-den', size: '24' };
        recommendation.secondary = { product: 'mini-stationary', size: '24' };
        return recommendation;
      }
      
      // Calm small dog who travels (road or air)
      if (!hasEscapeConcern) {
        recommendation.primary = { product: 'mini-stationary', size: '24' };
        if (fliesWithDog) {
          recommendation.caveat = 'small_dog_airline_cabin';
        }
        recommendation.secondary = { product: 'dog-den', size: '24' };
        return recommendation;
      }
      
      // Small dog with escape concern
      // Always recommend Mini Stationary as primary (right size)
      // Show High Anxiety as secondary if they want both options
      recommendation.primary = { product: 'mini-stationary', size: '24' };
      
      if (fliesWithDog) {
        // Escape + flying = no good solution
        recommendation.caveat = 'small_dog_escape_and_flying';
      } else {
        // Escape + road travel or no travel
        recommendation.caveat = 'small_dog_escape_concern';
      }
      
      // Always show High Anxiety as secondary for escape-prone small dogs
      // so they can see both options and decide
      recommendation.secondary = { product: 'high-anxiety', size: '30' };
      
      return recommendation;
    }

    // ESCAPE CONCERN PATHS
    if (hasEscapeConcern) {
      // Check if High Anxiety comes in this size
      const haHighAnxiety = PRODUCTS['high-anxiety'].sizes.includes(size);
      
      if (!travelsOften) {
        // Easy case: High Anxiety for home
        if (haHighAnxiety) {
          recommendation.primary = { product: 'high-anxiety', size };
        } else {
          // Fallback to collapsible with warning
          recommendation.primary = { product: 'collapsible-v2', size };
          recommendation.caveat = 'giant_breed_escape_warning';
        }
        return recommendation;
      }

      // Travels often WITH escape concern - CONFLICT
      if (fliesWithDog) {
        const airlineSizes = PRODUCTS['airline'].sizes;
        if (airlineSizes.includes(size)) {
          recommendation.primary = { product: 'high-anxiety', size };
          recommendation.caveat = 'escape_concern_flying_warning';
          recommendation.secondary = { product: 'airline', size };
        } else {
          recommendation.primary = { product: 'high-anxiety', size };
          recommendation.caveat = 'no_airline_size_available';
        }
        return recommendation;
      }

      // Road trips + escape concern - use priority
      if (priority === 'security') {
        recommendation.primary = { product: 'high-anxiety', size };
        recommendation.secondary = { product: 'collapsible-v2', size };
        return recommendation;
      }
      if (priority === 'portability') {
        recommendation.primary = { product: 'collapsible-v2', size };
        recommendation.caveat = 'collapsible_escape_warning';
        return recommendation;
      }
      if (priority === 'both') {
        recommendation.primary = { product: 'high-anxiety', size };
        recommendation.secondary = { product: 'collapsible-v2', size };
        recommendation.showBothEqual = true;
        return recommendation;
      }

      // Default for escape concern
      recommendation.primary = { product: 'high-anxiety', size };
      return recommendation;
    }

    // CALM DOG PATHS
    if (isCalm || !hasEscapeConcern) {
      if (fliesWithDog) {
        const airlineSizes = PRODUCTS['airline'].sizes;
        if (airlineSizes.includes(size)) {
          recommendation.primary = { product: 'airline', size };
          return recommendation;
        }
      }

      if (roadTrips || travelsOften) {
        recommendation.primary = { product: 'collapsible-v2', size };
        return recommendation;
      }

      // Calm, no travel - Dog Den if size exists
      const dogDenSizes = PRODUCTS['dog-den'].sizes;
      if (dogDenSizes.includes(size)) {
        recommendation.primary = { product: 'dog-den', size };
        recommendation.secondary = { product: 'collapsible-v2', size };
        return recommendation;
      }

      recommendation.primary = { product: 'collapsible-v2', size };
      return recommendation;
    }

    // DEFAULT
    recommendation.primary = { product: 'collapsible-v2', size };
    return recommendation;
  }

  function showResults() {
    const recommendation = getRecommendation();
    quizState.recommendation = recommendation;

    const { primary, secondary, caveat, showBothEqual } = recommendation;
    
    // Get product data from Shopify (passed from Liquid)
    const shopifyProductData = window.quizProductData || {};

    // Primary recommendation
    if (primary) {
      const product = PRODUCTS[primary.product];
      const shopifyData = shopifyProductData[primary.product] || {};
      const sizeLabel = SIZE_LABELS[primary.size];
      
      // Get variant URL with size pre-selected
      const variantUrl = buildProductUrl(primary.product, primary.size);
      
      document.querySelector('[data-result-title]').textContent = product.title;
      document.querySelector('[data-result-size]').textContent = `Recommended Size: ${sizeLabel}`;
      document.querySelector('[data-result-description]').textContent = product.description;
      
      // Build features list
      const featuresHtml = product.features.map(f => `
        <span class="quiz-result-card__feature">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
          ${f}
        </span>
      `).join('');
      document.querySelector('[data-result-features]').innerHTML = featuresHtml;
      
      document.querySelector('[data-result-cta]').href = variantUrl;
      
      // Set product image from Shopify
      const imgEl = document.querySelector('[data-result-image]');
      if (imgEl && shopifyData.image) {
        imgEl.src = shopifyData.image;
        imgEl.alt = product.title;
      }
    }

    // Caveat message
    if (caveat && CAVEATS[caveat]) {
      document.querySelector('[data-caveat-text]').textContent = CAVEATS[caveat];
      elements.resultsCaveat.style.display = 'block';
    } else {
      elements.resultsCaveat.style.display = 'none';
    }

    // Secondary recommendation
    if (secondary) {
      const product = PRODUCTS[secondary.product];
      const shopifyData = shopifyProductData[secondary.product] || {};
      const sizeLabel = SIZE_LABELS[secondary.size];
      const variantUrl = buildProductUrl(secondary.product, secondary.size);
      
      document.querySelector('[data-secondary-title]').textContent = product.title;
      document.querySelector('[data-secondary-size]').textContent = `Size: ${sizeLabel}`;
      document.querySelector('[data-secondary-description]').textContent = product.description;
      document.querySelector('[data-secondary-cta]').href = variantUrl;
      
      // Set secondary product image from Shopify
      const secondaryImgEl = document.querySelector('[data-secondary-image]');
      if (secondaryImgEl && shopifyData.image) {
        secondaryImgEl.src = shopifyData.image;
        secondaryImgEl.alt = product.title;
      }
      
      // Update "Also Consider" text if showing both as equal
      if (showBothEqual) {
        document.querySelector('.quiz-results__also-consider').textContent = 'For Travel';
      }
      
      elements.resultsSecondary.style.display = 'block';
    } else {
      elements.resultsSecondary.style.display = 'none';
    }

    // Track results
    trackEvent('quiz_complete', {
      recommendation: primary?.product,
      size: primary?.size,
      secondary: secondary?.product,
      dogName: quizState.answers.dogName,
      breed: quizState.answers.breed,
      breedSize: quizState.answers.breedSize,
      ...quizState.answers
    });

    // Send to Klaviyo
    sendToKlaviyo();
  }

  function buildProductUrl(productKey, size) {
    const product = PRODUCTS[productKey];
    if (!product) return '#';
    
    // Get Shopify product data (passed from Liquid)
    const shopifyProductData = window.quizProductData || {};
    const shopifyData = shopifyProductData[productKey] || {};
    
    // Build URL with variant ID if available
    let url = `/products/${product.handle}`;
    
    // Try to get variant ID from Shopify data
    if (shopifyData.variants && shopifyData.variants[size]) {
      url += `?variant=${shopifyData.variants[size]}`;
    } else {
      // Fallback to size parameter (PDP would need to handle this)
      const sizeMap = {
        '24': '24"',
        '30': '30"',
        '34': '34"',
        '40': '40"',
        '40+': '40"+',
        '48': '48"',
        '54': '54"'
      };
      if (size) {
        url += `?size=${encodeURIComponent(sizeMap[size] || size)}`;
      }
    }
    
    return url;
  }

  // ============================================
  // TRACKING & INTEGRATIONS
  // ============================================

  function trackEvent(eventName, data = {}) {
    // GA4
    if (typeof gtag !== 'undefined') {
      gtag('event', eventName, {
        event_category: 'crate_quiz',
        ...data
      });
    }

    // DataLayer for GTM
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ...data
    });

    // Klaviyo
    if (typeof _learnq !== 'undefined') {
      _learnq.push(['track', eventName, data]);
    }

    console.log('Quiz Event:', eventName, data);
  }

  function sendToKlaviyo() {
    const email = quizState.answers.email;
    const recommendation = quizState.recommendation;
    const listId = window.quizKlaviyoListId;
    
    console.log('Klaviyo Debug:', {
      email,
      listId,
      _learnq_exists: typeof _learnq !== 'undefined',
      klaviyo_exists: typeof klaviyo !== 'undefined'
    });

    // Prepare quiz data
    const quizData = {
      dogName: quizState.answers.dogName,
      concerns: quizState.answers.concerns,
      breed: quizState.answers.breed,
      breedSize: quizState.answers.breedSize,
      weight: quizState.answers.weight,
      travel: quizState.answers.travel,
      travelType: quizState.answers.travelType,
      priority: quizState.answers.priority,
      recommendedProduct: recommendation?.primary?.product,
      recommendedSize: recommendation?.primary?.size,
      secondaryProduct: recommendation?.secondary?.product,
      hasCaveat: !!recommendation?.caveat
    };

    // Method 1: Use _learnq if available (traditional Klaviyo snippet)
    if (typeof _learnq !== 'undefined') {
      console.log('Using _learnq for Klaviyo tracking');
      
      if (email) {
        _learnq.push(['identify', { '$email': email }]);
      }
      
      _learnq.push(['track', 'Crate Quiz Completed', quizData]);
    }
    
    // Method 2: Use klaviyo object if available (newer Klaviyo setup)
    if (typeof klaviyo !== 'undefined' && typeof klaviyo.push === 'function') {
      console.log('Using klaviyo object for tracking');
      
      if (email) {
        klaviyo.push(['identify', { '$email': email }]);
      }
      
      klaviyo.push(['track', 'Crate Quiz Completed', quizData]);
    }

    // Method 3: Subscribe to list via Klaviyo Client API (if list ID provided)
    if (email && listId) {
      console.log('Subscribing to Klaviyo list:', listId);
      
      // Use Klaviyo's public subscribe endpoint
      const subscribeData = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Crate Quiz',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  properties: {
                    'Quiz - Dog Name': quizState.answers.dogName,
                    'Quiz - Concerns': quizState.answers.concerns?.join(', '),
                    'Quiz - Breed': quizState.answers.breed,
                    'Quiz - Breed Size': quizState.answers.breedSize,
                    'Quiz - Weight': quizState.answers.weight,
                    'Quiz - Travel': quizState.answers.travel,
                    'Quiz - Travel Type': quizState.answers.travelType,
                    'Quiz - Priority': quizState.answers.priority,
                    'Quiz - Recommended Product': recommendation?.primary?.product,
                    'Quiz - Recommended Size': recommendation?.primary?.size
                  }
                }
              }
            }
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: listId
              }
            }
          }
        }
      };

      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + getKlaviyoCompanyId(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': '2024-02-15'
        },
        body: JSON.stringify(subscribeData)
      })
      .then(response => {
        console.log('Klaviyo subscription response:', response.status);
        if (!response.ok) {
          return response.text().then(text => {
            console.error('Klaviyo subscription error:', text);
          });
        }
        console.log('Successfully subscribed to Klaviyo list');
      })
      .catch(error => {
        console.error('Klaviyo subscription failed:', error);
      });
    } else if (!listId) {
      console.warn('No Klaviyo List ID configured in theme settings');
    } else if (!email) {
      console.log('No email provided, skipping list subscription');
    }
  }

  // Helper to get Klaviyo company ID from the page
  function getKlaviyoCompanyId() {
    // First try the setting from theme customizer
    if (window.quizKlaviyoCompanyId) {
      return window.quizKlaviyoCompanyId;
    }
    
    // Try to find in script tags
    const scripts = document.querySelectorAll('script[src*="klaviyo"]');
    for (const script of scripts) {
      const match = script.src.match(/company_id=([^&]+)/);
      if (match) return match[1];
    }
    
    // Check for Shopify Klaviyo app data
    if (window.klaviyoSettings && window.klaviyoSettings.companyId) {
      return window.klaviyoSettings.companyId;
    }
    
    console.error('Could not find Klaviyo company ID - please add it in theme settings');
    return null;
  }

  // ============================================
  // DOG NAME HANDLING
  // ============================================

  function initDogNameInput() {
    if (!elements.dogNameInput) return;

    elements.dogNameInput.addEventListener('input', () => {
      quizState.answers.dogName = elements.dogNameInput.value;
      validateCurrentStep();
    });
  }

  // ============================================
  // EMAIL HANDLING
  // ============================================

  function initEmailInput() {
    if (!elements.emailInput) return;

    elements.emailInput.addEventListener('input', () => {
      quizState.answers.email = elements.emailInput.value;
      if (emailValidationAttempted) {
        updateEmailValidation(true);
      }
      validateCurrentStep();
    });

    elements.emailInput.addEventListener('blur', () => {
      emailValidationAttempted = true;
      updateEmailValidation(true);
    });

    // Skip email button
    const skipBtn = elements.section.querySelector('[data-skip-email]');
    if (skipBtn) {
      skipBtn.addEventListener('click', () => {
        quizState.answers.email = null;
        showResults();
        showStep(11);
      });
    }
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  function initEventListeners() {
    // Option clicks
    elements.section.addEventListener('click', handleOptionClick);

    // Next buttons
    elements.section.querySelectorAll('[data-next]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        if (btn.hasAttribute('data-show-results')) {
          showResults();
        }
        nextStep();
      });
    });

    // Back buttons
    elements.section.querySelectorAll('[data-back]').forEach(btn => {
      btn.addEventListener('click', prevStep);
    });

    // Retake quiz button
    const retakeBtn = elements.section.querySelector('[data-retake-quiz]');
    if (retakeBtn) {
      retakeBtn.addEventListener('click', () => {
        resetQuiz();
      });
    }
  }

  function resetQuiz() {
    quizState = {
      currentStep: 1,
      totalSteps: 11,
      answers: {
        dogName: null,
        concerns: [],
        breed: null,
        breedSize: null,
        weight: null,
        travel: null,
        travelType: null,
        priority: null,
        email: null
      },
      recommendation: null
    };

    activeSteps = [1, 2, 3, 4, 6, 7, 10, 11];

    // Clear UI
    elements.section.querySelectorAll('.quiz-option').forEach(opt => {
      setOptionSelected(opt, false);
    });
    
    if (elements.dogNameInput) {
      elements.dogNameInput.value = '';
    }
    
    if (elements.breedInput) {
      elements.breedInput.value = '';
      elements.breedInput.placeholder = 'Search for a breed...';
    }

    if (elements.breedValue) {
      elements.breedValue.value = '';
    }

    elements.mixedBreedButton?.setAttribute('aria-pressed', 'false');
    hideBreedDropdown();
    
    if (elements.emailInput) {
      elements.emailInput.value = '';
      elements.emailInput.setAttribute('aria-invalid', 'false');
    }
    emailValidationAttempted = false;
    if (elements.emailError) elements.emailError.hidden = true;

    showStep(1);
    validateCurrentStep();
  }

  async function init() {
    if (!cacheElements()) {
      console.log('Crate quiz section not found');
      return;
    }

    // Load breed data first
    const loaded = await loadBreedData();
    if (!loaded) {
      console.warn('Failed to load breed data - quiz may have limited functionality');
    }

    initEventListeners();
    initDogNameInput();
    initBreedSearch();
    initEmailInput();
    elements.section.querySelectorAll('.quiz-option').forEach(option => {
      setOptionSelected(option, option.classList.contains('quiz-option--selected'));
    });
    showStep(1, {
      shouldAnnounce: false,
      shouldFocus: false,
      shouldScroll: false,
      shouldTrack: false
    });
    validateCurrentStep();

    console.log('Crate Quiz initialized');
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
